// ===============================================================================
// SearchExpenses.js - Expense Search Page for SplitMon (Premium Feature)
// ===============================================================================
// Location: /var/www/wow/frontend/src/SearchExpenses.js
// Advanced expense search across all groups with multiple filters
// UPDATED: Added business group/expense search support
// - Groups filter shows both personal and business groups with visual distinction
// - Category filter combines personal text categories + business categories
// - Results show group type badge (personal vs business)
// - Uses composite keys (group_type + id) to prevent ID collisions
// ===============================================================================

import React, { useState, useEffect } from "react";
import { searchAPI, formatCurrency, formatDate } from "./api";
import "./SearchExpenses.css";

// Category display mapping (personal groups)
const CATEGORY_DISPLAY = {
  accommodation: "🏠 Accommodation",
  finance: "💳 Banking & Finance",
  business: "💼 Business Expenses",
  automotive: "🔧 Car Maintenance",
  cleaning: "🧺 Cleaning & Laundry",
  education: "🎓 Education",
  entertainment: "🎬 Entertainment",
  food: "🍕 Food & Drinks",
  fuel: "⛽ Fuel & Petrol",
  garden: "🌳 Garden",
  gifts: "🎁 Gifts & Presents",
  groceries: "🛒 Groceries",
  health: "💊 Health",
  houseexpense: "🏠 House Expenses",
  nightlife: "🎶 Nightlife",
  personalcare: "✂️ Personal Care",
  pets: "🐶 Pet Expenses",
  shopping: "👗 Shopping",
  supplements: "🌿 Supplements",
  subscriptions: "📱 Subscriptions",
  taxes: "🧾 Taxes",
  telecom: "📶 Telecommunication",
  transport: "🚗 Transport",
  travels: "✈️ Travels & Vacations",
  utilities: "💡 Utilities",
  other: "📦 Other"
};

// Helper function to extract error message from API response
const getErrorMessage = (err, defaultMessage) => {
  // Handle Pydantic validation errors (array of objects)
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    
    // If detail is an array (Pydantic validation errors)
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    
    // If detail is a string
    if (typeof detail === 'string') {
      return detail;
    }
    
    // If detail is an object with a message
    if (typeof detail === 'object' && detail.msg) {
      return detail.msg;
    }
  }
  
  // Fallback to error message or default
  return err?.message || defaultMessage;
};

function SearchExpenses({ user, onBack }) {
  // ===============================================================================
  // State
  // ===============================================================================
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  
  // Search form state - separate personal and business group selections
  const [selectedPersonalGroups, setSelectedPersonalGroups] = useState([]);
  const [selectedBizGroups, setSelectedBizGroups] = useState([]);
  const [description, setDescription] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [category, setCategory] = useState("");       // "food" or "biz:42" or "biz_custom:7"
  const [currency, setCurrency] = useState("");
  const [paidByUserId, setPaidByUserId] = useState("");
  
  // Results state
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Expanded expense details (composite key: "personal_123" or "business_456")
  const [expandedExpense, setExpandedExpense] = useState(null);

  const LIMIT = 20;

  // ===============================================================================
  // Derived data from filters
  // ===============================================================================
  const personalGroups = filters?.groups?.filter(g => g.group_type === "personal") || [];
  const businessGroups = filters?.groups?.filter(g => g.group_type === "business") || [];
  const hasPersonalGroups = personalGroups.length > 0;
  const hasBusinessGroups = businessGroups.length > 0;

  // ===============================================================================
  // Load Filters on Mount
  // ===============================================================================
  useEffect(() => {
    loadFilters();
  }, []);

  const loadFilters = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await searchAPI.getFilters();
      if (res.data?.ok) {
        setFilters(res.data);
      } else {
        setError("Failed to load search filters");
      }
    } catch (err) {
      console.error("Load filters error:", err);
      const errorMsg = getErrorMessage(err, "Failed to load search filters. Please make sure the backend is running.");
      setError(errorMsg);
    }
    setLoading(false);
  };

  // ===============================================================================
  // Search Handler
  // ===============================================================================
  const handleSearch = async (newSearch = true) => {
    setSearching(true);
    setError("");
    
    const searchOffset = newSearch ? 0 : offset;
    
    try {
      // Parse category selection into the right params
      let categoryParam = null;
      let bizCategoryId = null;
      let bizCustomCategoryId = null;
      
      if (category) {
        if (category.startsWith("biz_custom:")) {
          bizCustomCategoryId = parseInt(category.split(":")[1]);
        } else if (category.startsWith("biz:")) {
          bizCategoryId = parseInt(category.split(":")[1]);
        } else {
          categoryParam = category;
        }
      }
      
      const searchParams = {
        group_ids: selectedPersonalGroups.length > 0 ? selectedPersonalGroups : null,
        business_group_ids: selectedBizGroups.length > 0 ? selectedBizGroups : null,
        description: description.trim() || null,
        date_from: dateFrom || null,
        date_to: dateTo || null,
        category: categoryParam,
        business_category_id: bizCategoryId,
        business_custom_category_id: bizCustomCategoryId,
        currency: currency || null,
        paid_by_user_id: paidByUserId ? parseInt(paidByUserId) : null,
        limit: LIMIT,
        offset: searchOffset,
        sort_by: "expense_date",
        sort_order: "desc"
      };
      
      const res = await searchAPI.search(searchParams);
      
      if (res.data?.ok) {
        if (newSearch) {
          setResults(res.data.expenses);
          setOffset(LIMIT);
        } else {
          setResults([...results, ...res.data.expenses]);
          setOffset(searchOffset + LIMIT);
        }
        setTotalResults(res.data.total);
        setHasMore(res.data.has_more);
        setHasSearched(true);
      }
    } catch (err) {
      console.error("Search error:", err);
      const errorMsg = getErrorMessage(err, "Search failed. Please try again.");
      setError(errorMsg);
    }
    
    setSearching(false);
  };

  const handleLoadMore = () => {
    handleSearch(false);
  };

  // ===============================================================================
  // Reset Filters
  // ===============================================================================
  const handleReset = () => {
    setSelectedPersonalGroups([]);
    setSelectedBizGroups([]);
    setDescription("");
    setDateFrom("");
    setDateTo("");
    setCategory("");
    setCurrency("");
    setPaidByUserId("");
    setResults([]);
    setTotalResults(0);
    setHasMore(false);
    setOffset(0);
    setHasSearched(false);
    setExpandedExpense(null);
  };

  // ===============================================================================
  // Group Selection Handlers
  // ===============================================================================
  const handlePersonalGroupToggle = (groupId) => {
    setSelectedPersonalGroups(prev => 
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleBizGroupToggle = (groupId) => {
    setSelectedBizGroups(prev => 
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  // ===============================================================================
  // Composite key for expanded expense (prevents ID collision across databases)
  // ===============================================================================
  const getExpenseKey = (expense) => `${expense.group_type}_${expense.id}`;

  // ===============================================================================
  // Get display category for a result (works for both personal and business)
  // ===============================================================================
  const getDisplayCategory = (expense) => {
    if (expense.group_type === "business") {
      // Business: category is already the resolved name from backend
      const icon = expense.category_icon || "📦";
      return `${icon} ${expense.category || "Uncategorized"}`;
    }
    // Personal: use the display mapping
    return CATEGORY_DISPLAY[expense.category] || expense.category || "Other";
  };

  const getCategoryIcon = (expense) => {
    if (expense.group_type === "business") {
      return expense.category_icon || "📦";
    }
    return CATEGORY_DISPLAY[expense.category]?.split(" ")[0] || "📦";
  };

  // ===============================================================================
  // Render Loading
  // ===============================================================================
  if (loading) {
    return (
      <div className="search-expenses-page">
        <div className="page-loading">
          <div className="spinner"></div>
          <p>Loading search options...</p>
        </div>
      </div>
    );
  }

  // ===============================================================================
  // Render Error State (when filters failed to load)
  // ===============================================================================
  if (!filters && error) {
    return (
      <div className="search-expenses-page">
        <div className="page-header">
          <div className="header-title">
            <h1>🔍 Search Expenses</h1>
            <span className="premium-badge">⭐ Premium</span>
          </div>
        </div>
        <div className="error-card">
          <div className="error-icon">⚠️</div>
          <h3>Failed to Load</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadFilters}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ===============================================================================
  // Selected groups summary for hint text
  // ===============================================================================
  const totalSelectedGroups = selectedPersonalGroups.length + selectedBizGroups.length;
  const groupHintText = totalSelectedGroups === 0
    ? "All groups selected"
    : `${totalSelectedGroups} group(s) selected`;

  // ===============================================================================
  // Render
  // ===============================================================================
  return (
    <div className="search-expenses-page">
      {/* Header */}
      <div className="biz-analysis-header">
        <div className="biz-header-content">
          <div className="biz-header-title">
            <h1>🔍 Search Expenses</h1>
            <span className="biz-premium-badge">⭐ Premium</span>
          </div>
          <p className="biz-header-subtitle">
            Search across all your personal and business groups with advanced filters
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError("")}>✕</button>
        </div>
      )}

      {/* Search Filters Card */}
      <div className="search-card">
        <h2>Search Filters</h2>
        
        <div className="filters-grid">
          {/* Groups Filter */}
          <div className="filter-group filter-full">
            <label>Groups</label>
            <div className="group-chips">
              {/* Personal Groups */}
              {hasPersonalGroups && personalGroups.map(group => (
                <button
                  key={`personal_${group.id}`}
                  className={`group-chip ${selectedPersonalGroups.includes(group.id) ? 'selected' : ''}`}
                  onClick={() => handlePersonalGroupToggle(group.id)}
                  title={`Personal · ${group.currency} · ${group.member_count} member(s)`}
                >
                  <span className="chip-icon">{group.name.charAt(0)}</span>
                  <span className="chip-text">{group.name}</span>
                  {selectedPersonalGroups.includes(group.id) && <span className="chip-check">✔</span>}
                </button>
              ))}
              
              {/* Business Groups */}
              {hasBusinessGroups && businessGroups.map(group => (
                <button
                  key={`business_${group.id}`}
                  className={`group-chip business-chip ${selectedBizGroups.includes(group.id) ? 'selected' : ''}`}
                  onClick={() => handleBizGroupToggle(group.id)}
                  title={`Business · ${group.currency} · ${group.member_count} member(s)`}
                >
                  <span className="chip-icon chip-icon-biz">🏢</span>
                  <span className="chip-text">{group.name}</span>
                  <span className="chip-type-badge">Biz</span>
                  {selectedBizGroups.includes(group.id) && <span className="chip-check">✔</span>}
                </button>
              ))}
              
              {(!hasPersonalGroups && !hasBusinessGroups) && (
                <span className="no-groups">No groups available</span>
              )}
            </div>
            <small className="filter-hint">{groupHintText}</small>
          </div>

          {/* Description Search */}
          <div className="filter-group filter-full">
            <label>Description</label>
            <input
              type="text"
              placeholder="Search in expense descriptions..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="filter-input"
            />
          </div>

          {/* Date Range */}
          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="filter-input"
            />
          </div>

          {/* Category - Combined personal + business with optgroups */}
          <div className="filter-group">
            <label>Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="filter-select"
            >
              <option value="">All Categories</option>
              
              {/* Personal Categories */}
              {filters?.categories?.length > 0 && (
                <optgroup label="Personal Categories">
                  {filters.categories.map(cat => (
                    <option key={cat} value={cat}>
                      {CATEGORY_DISPLAY[cat] || cat}
                    </option>
                  ))}
                </optgroup>
              )}
              
              {/* Business Preset Categories */}
              {filters?.business_categories?.filter(c => !c.is_custom).length > 0 && (
                <optgroup label="Business Categories">
                  {filters.business_categories
                    .filter(c => !c.is_custom)
                    .map(cat => (
                      <option key={`biz_${cat.id}`} value={`biz:${cat.id}`}>
                        {cat.icon} {cat.name}
                      </option>
                    ))
                  }
                </optgroup>
              )}
              
              {/* Business Custom Categories */}
              {filters?.business_categories?.filter(c => c.is_custom).length > 0 && (
                <optgroup label="Custom Business Categories">
                  {filters.business_categories
                    .filter(c => c.is_custom)
                    .map(cat => (
                      <option key={`biz_custom_${cat.id}`} value={`biz_custom:${cat.id}`}>
                        {cat.icon} {cat.name}
                      </option>
                    ))
                  }
                </optgroup>
              )}
            </select>
          </div>

          {/* Currency */}
          <div className="filter-group">
            <label>Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="filter-select"
            >
              <option value="">All Currencies</option>
              {filters?.currencies?.map(curr => (
                <option key={curr} value={curr}>{curr}</option>
              ))}
            </select>
          </div>

          {/* Paid By */}
          <div className="filter-group filter-full">
            <label>Paid By</label>
            <select
              value={paidByUserId}
              onChange={(e) => setPaidByUserId(e.target.value)}
              className="filter-select"
            >
              <option value="">Anyone</option>
              {filters?.members?.map(member => (
                <option key={member.user_id} value={member.user_id}>
                  {member.name} (@{member.username})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search Actions */}
        <div className="search-actions">
          <button 
            className="btn btn-secondary"
            onClick={handleReset}
          >
            🔄 Reset
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => handleSearch(true)}
            disabled={searching}
          >
            {searching ? "Searching..." : "🔍 Search Expenses"}
          </button>
        </div>
      </div>

      {/* Results Section */}
      {hasSearched && (
        <div className="results-section">
          <div className="results-header">
            <h2>Search Results</h2>
            <span className="results-count">{totalResults} expense(s) found</span>
          </div>

          {results.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">🔭</div>
              <h3>No expenses found</h3>
              <p>Try adjusting your search filters</p>
            </div>
          ) : (
            <>
              <div className="results-list">
                {results.map(expense => {
                  const expKey = getExpenseKey(expense);
                  return (
                    <div 
                      key={expKey} 
                      className={`expense-result-card ${expandedExpense === expKey ? 'expanded' : ''} ${expense.group_type === 'business' ? 'business-expense-card' : ''}`}
                    >
                      <div 
                        className="expense-main"
                        onClick={() => setExpandedExpense(expandedExpense === expKey ? null : expKey)}
                      >
                        <div className="expense-left">
                          <div className={`expense-category-icon ${expense.group_type === 'business' ? 'biz-category-icon' : ''}`}>
                            {getCategoryIcon(expense)}
                          </div>
                          <div className="expense-info">
                            <h4 className="expense-description">{expense.description}</h4>
                            <div className="expense-meta">
                              <span className={`meta-group ${expense.group_type === 'business' ? 'meta-group-biz' : ''}`}>
                                {expense.group_type === "business" && <span className="meta-biz-icon">🏢 </span>}
                                {expense.group_name}
                              </span>
                              <span className="meta-separator">•</span>
                              <span className="meta-date">{formatDate(expense.expense_date)}</span>
                              <span className="meta-separator">•</span>
                              <span className="meta-payer">
                                {expense.multiple_payers ? "Multiple payers" : `Paid by ${expense.paid_by_name}`}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="expense-right">
                          <div className="expense-amount">
                            {formatCurrency(expense.amount, expense.currency)}
                          </div>
                          <div className="expense-expand-icon">
                            {expandedExpense === expKey ? "▲" : "▼"}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedExpense === expKey && (
                        <div className="expense-details">
                          <div className="details-grid">
                            <div className="detail-item">
                              <label>Category</label>
                              <span>{getDisplayCategory(expense)}</span>
                            </div>
                            <div className="detail-item">
                              <label>Split Type</label>
                              <span className="search-split-badge">{expense.split_type}</span>
                            </div>
                            <div className="detail-item">
                              <label>Group Type</label>
                              <span className={`search-type-badge ${expense.group_type === 'business' ? 'type-business' : 'type-personal'}`}>
                                {expense.group_type === "business" ? "🏢 Business" : "👥 Personal"}
                              </span>
                            </div>
                          </div>

                          {/* Payers */}
                          {expense.payers && expense.payers.length > 0 && (
                            <div className="detail-section">
                              <h5>Payers</h5>
                              <div className="payers-list">
                                {expense.payers.map(payer => (
                                  <div key={payer.user_id} className="payer-item">
                                    <span className="payer-name">{payer.name}</span>
                                    <span className="payer-amount">
                                      {formatCurrency(payer.amount, expense.currency)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Splits */}
                          {expense.splits && expense.splits.length > 0 && (
                            <div className="detail-section">
                              <h5>Split Between</h5>
                              <div className="splits-list">
                                {expense.splits.map(split => (
                                  <div key={split.user_id} className="split-item">
                                    <span className="split-name">{split.name}</span>
                                    <div className="split-info">
                                      <span className="split-amount">
                                        {formatCurrency(split.amount, expense.currency)}
                                      </span>
                                      {expense.split_type === "shares" && (
                                        <span className="split-shares">({split.shares} share{split.shares !== 1 ? 's' : ''})</span>
                                      )}
                                      {expense.split_type === "percentage" && (
                                        <span className="split-percentage">({split.split_percentage}%)</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Load More */}
              {hasMore && (
                <div className="load-more-section">
                  <button 
                    className="btn btn-secondary"
                    onClick={handleLoadMore}
                    disabled={searching}
                  >
                    {searching ? "Loading..." : "Load More Results"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchExpenses;

